import type {
  DiscoveryActivityGroup,
  DiscoveryBookableCard,
  DiscoverySlot,
} from "@/actions/venue-discovery";
import type { SeeMoreChip } from "@/components/ui/see-more-card";
import { isAllDayWindow } from "@/features/venue/engine/time";
import { compareClassTime } from "@/lib/discovery/temporal-priority";
import { nameChips, nearestDistanceLabel } from "@/lib/format-name-list";
import { venueActivityHref } from "@/lib/venue-links";

/**
 * Where a start lands: the experience's booking page with that instant
 * preselected, so a tap arrives on the slot it named rather than a fresh grid.
 */
export const bookableSlotHref = (
  card: DiscoveryBookableCard,
  startIso: string,
) =>
  venueActivityHref({
    hiiveSlug: card.hiiveSlug,
    venueSlug: card.venueSlug,
    activityId: card.activityId,
    experienceSlug: card.experienceSlug,
    start: startIso,
  });

/** The experience page with nothing preselected — the whole time picker. */
export const experiencePageHref = (card: DiscoveryBookableCard) =>
  venueActivityHref({
    hiiveSlug: card.hiiveSlug,
    venueSlug: card.venueSlug,
    activityId: card.activityId,
    experienceSlug: card.experienceSlug,
  });

/** "Tomorrow" mid-sentence is "tomorrow"; a weekday or date keeps its capital. */
const inlineDay = (dayLabel: string) =>
  dayLabel === "Tomorrow" || dayLabel === "Today"
    ? dayLabel.toLowerCase()
    : dayLabel;

const isAllDaySlot = (slot: DiscoverySlot) =>
  isAllDayWindow(new Date(slot.startIso), new Date(slot.endIso));

/** Monday-first, the way a timetable is read. 0 = Sun … 6 = Sat. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * A timetable's days in words: "every day", "weekdays", "weekends",
 * "Mon – Sat" for a run of three or more, else "Mon · Wed · Fri".
 * null for an empty timetable.
 */
export function weeklyPattern(weekdays: number[]): string | null {
  const days = WEEK_ORDER.filter((day) => weekdays.includes(day));
  if (days.length === 0) return null;
  if (days.length === 7) return "every day";
  const key = days.join(",");
  if (key === "1,2,3,4,5") return "weekdays";
  if (key === "6,0") return "weekends";
  const first = WEEK_ORDER.indexOf(days[0] as number);
  const contiguous = days.every((day, i) => WEEK_ORDER[first + i] === day);
  if (contiguous && days.length >= 3) {
    return `${DAY_SHORT[days[0] as number]} – ${DAY_SHORT[days.at(-1) as number]}`;
  }
  return days.map((day) => DAY_SHORT[day]).join(" · ");
}

/**
 * The line under the Book row: one fact about the other options, and an
 * invitation to go and choose one.
 *
 * It is a nudge, not an inventory. "Also 5 PM +2" listed times without saying
 * why you would tap; "+1 more day" undersold a pass open all week, because
 * the 48-hour sweep behind these cards only ever sees today and tomorrow. So
 * the fact comes from whichever source says the most — more starts on the
 * next start's day, then the weekly timetable, then tomorrow — and the action
 * names what the page lets you do with it.
 *
 * `info` is short enough to sit beside `cta` on a grid tile.
 */
export function moreOptions(card: DiscoveryBookableCard): {
  info: string;
  cta: string;
} {
  const [next, ...rest] = card.slots;
  const pattern = weeklyPattern(card.weekdays);
  const allDay = card.slots.length > 0 && card.slots.every(isAllDaySlot);

  if (!next) return { info: "More dates", cta: "See dates" };

  // More sessions on the same day as the one on the Book row. Counted from
  // the uncapped total where every start is on that day; otherwise from the
  // capped list, which is still exact for a single day in practice.
  const sameDay = rest.filter((slot) => slot.dayLabel === next.dayLabel);
  if (!allDay && sameDay.length > 0) {
    const count =
      sameDay.length === rest.length ? card.slotCount - 1 : sameDay.length;
    return {
      info: `${count} more ${inlineDay(next.dayLabel)}`,
      cta: "See times",
    };
  }

  if (pattern === "every day") {
    return {
      info: allDay ? "Open every day" : "Runs every day",
      cta: "Pick a day",
    };
  }

  // The other days of the week it runs, beyond the one on the Book row.
  const otherDays = card.weekdays.filter((day) => day !== next.weekday);
  if (otherDays.length > 0) {
    // A scattered week spelled out ("Mon · Wed · Fri · Sat") clips on a grid
    // tile; its size says the same thing in half the width.
    let info = `Also ${weeklyPattern(otherDays)}`;
    if (otherDays.length > 2) {
      info = pattern?.includes(" · ")
        ? `${card.weekdays.length} days a week`
        : `${allDay ? "Open" : "Runs"} ${pattern}`;
    }
    return { info, cta: "See days" };
  }

  if (rest.length > 0) {
    return {
      info: `More ${inlineDay(rest[0]?.dayLabel ?? "")}`,
      cta: "See times",
    };
  }

  if (pattern) {
    return {
      info: `Every ${DAY_LONG[next.weekday]}`,
      cta: "See dates",
    };
  }

  return { info: "More dates", cta: "See dates" };
}

/** Every start the card knows about, for the more link's tooltip. */
export function allTimesTitle(card: DiscoveryBookableCard): string {
  return card.slots
    .map((slot) => `${slot.dayLabel} ${slot.rangeLabel}`)
    .join("\n");
}

/**
 * What a slot row prints: the window, not just its opening minute.
 *
 * "7:00 AM" alone on a class leaves you guessing whether you are free at
 * 7:45; the end time is the half of the answer the pill used to drop. When
 * both ends share a meridiem it is said once ("7:00 – 8:00 AM"), which is what
 * lets the range and a Book button share a row half a phone wide.
 *
 * An open-entry window is still "All day" — spelling out "6:00 AM – 10:00 PM"
 * for a pass you can use any time answers a question nobody asked.
 *
 * String work on the server-formatted labels only, so server and client render
 * the same text whatever the device's zone.
 */
export function slotRowLabel(slot: DiscoverySlot): string {
  if (isAllDayWindow(new Date(slot.startIso), new Date(slot.endIso))) {
    return slot.timeLabel;
  }
  const match = /^(.+) (AM|PM) – (.+) (AM|PM)$/.exec(slot.rangeLabel);
  if (match && match[2] === match[4]) {
    return `${match[1]} – ${match[3]} ${match[4]}`;
  }
  return slot.rangeLabel;
}

/**
 * Activity groups, flattened into one browsable list.
 *
 * Round-robin rather than concatenated: the groups arrive sorted by which
 * opens soonest, so a straight concat puts every yoga card before the first
 * badminton one and the rail reads as a single activity to anybody who does
 * not swipe to the end. Taking one card from each group in turn keeps the
 * first screenful varied, which is the whole argument for a discovery rail.
 */
export function flattenBookableCards(
  groups: DiscoveryActivityGroup[],
  limit?: number,
): DiscoveryBookableCard[] {
  const deepest = groups.reduce(
    (most, group) => Math.max(most, group.cards.length),
    0,
  );
  const flattened: DiscoveryBookableCard[] = [];

  for (let depth = 0; depth < deepest; depth += 1) {
    for (const group of groups) {
      const card = group.cards[depth];
      if (card) flattened.push(card);
    }
  }

  // Activity mixing is allowed only inside the same market day/time window.
  flattened.sort(
    (a, b) =>
      compareClassTime(a, b) ||
      (b.discoveryScore ?? 0) - (a.discoveryScore ?? 0),
  );
  return limit == null ? flattened : flattened.slice(0, limit);
}

/**
 * The see-more tile's chips for the cards it stands in for: how many can
 * still be booked today, how close the nearest is, which activities, and how
 * many venues. Each only when true, so a tile never says "0 open today".
 */
export function hiddenCardChips(cards: DiscoveryBookableCard[]): SeeMoreChip[] {
  const openToday = cards.filter((card) =>
    card.slots.some((slot) => slot.dayLabel === "Today"),
  ).length;
  const nearest = nearestDistanceLabel(cards.map((card) => card.distanceKm));
  const venues = new Set(cards.map((card) => card.venueId)).size;
  return [
    ...(openToday > 0
      ? [{ label: `${openToday} open today`, tone: "brand" as const }]
      : []),
    ...(nearest ? [{ label: nearest, tone: "amber" as const }] : []),
    ...nameChips(
      cards.map((card) => card.activityName),
      3,
    ).map((label) => ({ label })),
    ...(venues > 1
      ? [{ label: `${venues} venues`, tone: "emerald" as const }]
      : []),
  ];
}
